import { getHubConfig } from 'const/HubConfig';
import { MapAreaOverlay } from 'const/Map/MapAreaOverlay';
import { MapStyle } from 'const/Map/MapStyle';
import { useAreaOverlay } from 'hooks/useAreaOverlay';
import {
  GeoJSONSource,
  LngLatBounds,
  LngLatLike,
  Map,
  Marker,
  Popup,
} from 'mapbox-gl';
import {
  PlannerBundle,
  PlannerOrder,
  WindowGroups,
} from 'pages/planner/utils/usePlannerData';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BundleJson } from 'types/bundleTypes';
import { Region } from 'types/orderTypes';
import { BundleMarker } from './BundleMarker/BundleMarker';
import { HubMarker } from './HubMarker/HubMarker';
import { MapboxLegend } from './Legend/Legend';
import { MultiMarker } from './MultiMarker/MultiMarker';
import { OrdersMarker } from './OrdersMarker/OrdersMarker';
import { DrawPolygon } from './DrawPolygon/DrawPolygon';
import { FeatureCollection } from 'geojson';
import {
  convertOrdersToGeoOrder,
  GeoOrders,
} from 'pages/planner/map/utils/mapUtils';
import { useZonesOverlay } from 'hooks/useZonesOverlay';
import {
  ZonesOverlay,
  ZonesOverlayFeatureStateObject,
} from './ZonesOverlay/ZonesOverlay';
import { MapZonesType } from 'const/Map/MapZonesType';
import { Pinpoint } from './Pinpoint/Pinpoint';
import { Validations } from './Validations/validations';

const initialPositionsWithZoom: {
  [region in Region]: [number, number, number];
} = {
  OSLO: [8.88, 59.9438, 10.8072],
  VESTFOLD: [8.76, 59.2814, 10.2439],
  BERGEN: [9.85, 60.3587, 5.3078],
  STAVANGER: [9.57, 58.8797, 5.696],
  TRONDHEIM: [10.39, 63.4215, 10.4335],
  GRENLAND: [9.8, 59.1289, 9.585],
  OSTFOLD: [8.43, 59.526, 11.065],
  KRISTIANSAND: [8.62, 58.2381, 7.6278],
};

export interface MapboxBasicProps {
  region: Region;
  initialPosition?: LngLatLike | undefined; // TODO: Remove.
  fitToAreaOverlayBounds?: boolean; // TODO: Always false. Remove functionality
  enableDraw?: boolean;
  windowGroups?: WindowGroups;
  hasLegend?: boolean;
  hasOrders?: boolean;
  hasMultiMarker?: boolean;
  hasBundle?: boolean;
  hasHubMarker?: boolean;
  hasAreaOverlay?: boolean;
  hasZonesOverlay?: boolean;
  hasPinpoint?: boolean;
  orders?: PlannerOrder[][];
  selectedOrders?: PlannerOrder[];
  selectedOrderIds?: string[];
  multiMarkers?: PlannerOrder[][];
  bundles?: PlannerBundle[];
  fetchedBundles?: BundleJson[];
  shouldClearPolygon?: boolean;
  zonesOverlayType?: MapZonesType;
  zonesOverlayObjects?: Array<ZonesOverlayFeatureStateObject>;
  zonesOverlayCountData?: Record<string, number>;
  zonesOverlayHideLabel?: boolean;
  pinpointData?: {
    lon: number;
    lat: number;
  };
  pinpointDraggable?: boolean;
  pinpointIsUsingHubAsDefault?: boolean;
  onClickOrderMarker?: (orders: PlannerOrder[]) => void;
  updateBundleFromPusher?: () => void;
  resetPolygonDrawing?: () => void;
  onCreatePolygonDrawing?: (
    polygonData: FeatureCollection,
    orders: GeoOrders
  ) => void;
  onUpdatePolygonDrawing?: (
    polygonData: FeatureCollection,
    orders: GeoOrders
  ) => void;
  onClickZonesOverlay?: (
    selectedZone: string | number,
    currentFeatureState: any
  ) => void;
  onDragEndPinpoint?: (newPinpointData: { lon: number; lat: number }) => void;
}

export const MapboxBasic = ({ ...props }: MapboxBasicProps) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<Map>(null);
  const reloadMapStyle = useRef<boolean>(false);

  const hoverPopupRef = useRef(new Popup({ offset: 15, anchor: 'top' }));
  const rightClickPopupRef = useRef(new Popup({ offset: 15, anchor: 'top' }));

  const [mapStyle, setMapStyle] = useState<MapStyle>(MapStyle.Normal);
  const [styleLoaded, setStyleLoaded] = useState(false);

  const areaOverlay = useAreaOverlay(props.region, !props.hasAreaOverlay);

  const zonesOverlay = useZonesOverlay(props.region, !props.hasZonesOverlay);

  /* This ref will be used for switching the Region */
  const oldAreaOverlay = useRef(JSON.stringify(areaOverlay));
  const oldRegion = useRef(props.region);
  const hubMarker = useRef<Marker>();

  /* Shared values inside children */
  const windows = props.windowGroups ? Array.from(props.windowGroups) : [];
  const ordersGeo =
    props.orders &&
    convertOrdersToGeoOrder(props.orders, props.selectedOrderIds);
  const multiMarkersGeo =
    props.multiMarkers &&
    convertOrdersToGeoOrder(props.multiMarkers, props.selectedOrderIds);

  const changeMapStyle = (style: MapStyle) => {
    setStyleLoaded(false);
    setMapStyle(style);
    map.current.setStyle(style);
    reloadMapStyle.current = true;
  };

  const resetAreaOverlayLayer = useCallback(() => {
    map.current.getLayer(MapAreaOverlay.Layer) &&
      map.current.removeLayer(MapAreaOverlay.Layer);
    map.current.getLayer(MapAreaOverlay.Border) &&
      map.current.removeLayer(MapAreaOverlay.Border);
    map.current.boxZoom.disable();
  }, []);

  const addAreaOverlay = useCallback(
    (focus?: boolean) => {
      /* Wrapper */
      if (map.current.getSource(MapAreaOverlay.Id)) {
        (map.current.getSource(MapAreaOverlay.Id) as GeoJSONSource).setData(
          areaOverlay
        );
      } else {
        map.current.addSource(MapAreaOverlay.Id, {
          type: 'geojson',
          data: areaOverlay,
        });
      }

      resetAreaOverlayLayer();

      map.current.addLayer({
        id: MapAreaOverlay.Layer,
        type: 'fill',
        source: MapAreaOverlay.Id,
        layout: {},
        paint: {
          'fill-color': 'black',
          'fill-opacity': 0.4,
        },
      });

      map.current.addLayer({
        id: MapAreaOverlay.Border,
        type: 'line',
        source: MapAreaOverlay.Id,
        layout: {},
        paint: {
          'line-color': '#661aff',
          'line-width': 2,
        },
      });

      if (props.fitToAreaOverlayBounds || focus) {
        const coordinates = (areaOverlay?.features[0].geometry as any)
          ?.coordinates[0][1];

        if (coordinates) {
          const bounds = new LngLatBounds(coordinates[0], coordinates[0]);

          coordinates.map(coord => {
            bounds.extend(coord);

            return coord;
          });

          map.current.fitBounds(bounds);
        }
      }
    },
    [areaOverlay, props.fitToAreaOverlayBounds, resetAreaOverlayLayer]
  );

  const addHubPosition = useCallback(() => {
    const hubPosition = getHubConfig(props.region)?.hubAddress?.location;

    hubMarker.current = new Marker(HubMarker, { anchor: 'bottom' });
    hubMarker.current
      .setLngLat([hubPosition.longitude, hubPosition.latitude])
      .addTo(map.current);
  }, [props.region]);

  /* Map Initialization */
  useEffect(() => {
    /* Initialize map only once */
    if (map.current) return;

    /* Note : this Map Initialization will showing a browser warning */
    /* [Violation] 'requestAnimationFrame' handler */
    const initialPosition = initialPositionsWithZoom[props.region];
    map.current = new Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [initialPosition[2], initialPosition[1]],
      zoom: initialPosition[0],
      hash: true,
      maxPitch: 0,
      dragRotate: false,
    });
  }, [mapStyle, props.region]);

  /* Add Zones and Hub Position after Map Initialization */
  useEffect(() => {
    if (!map.current) return;

    const activeArea = () => {
      props.hasAreaOverlay && areaOverlay && addAreaOverlay();
      props.hasHubMarker && addHubPosition();
      setStyleLoaded(true);
    };
    map.current.on('load', activeArea);

    return () => {
      map.current.off('load', activeArea);
    };
  }, [
    areaOverlay,
    addAreaOverlay,
    addHubPosition,
    props.hasHubMarker,
    props.hasAreaOverlay,
    props.region,
  ]);

  /* Reload the Map after MapStyle changed (between Normal Map and Satelite Map) */
  useEffect(() => {
    if (!map.current) return;

    const reloadActiveArea = () => {
      if (reloadMapStyle.current) {
        props.hasAreaOverlay && addAreaOverlay();
        setStyleLoaded(true);
        reloadMapStyle.current = false;
      }
    };
    map.current.on('styledata', reloadActiveArea);

    return () => {
      map.current.off('styledata', reloadActiveArea);
    };
  }, [addAreaOverlay, props.hasAreaOverlay, props.region]);

  /* Detecting if the Region has been switched */
  /* This functionality should not breaking the coordinate focus feature based on URL hash */
  /* (see fitToAreaOverlayBounds in AreaOverlay) */
  useEffect(() => {
    if (!map.current) return;

    if (oldAreaOverlay.current !== JSON.stringify(areaOverlay)) {
      /* Always replace `null` to `actual value` on FirstInit */
      oldAreaOverlay.current = JSON.stringify(areaOverlay);

      /* Only run when it's not FirstInit */
      /* because oldRegion's FirstInit always have the same value with props.region */
      if (oldRegion.current !== props.region) {
        oldRegion.current = props.region;

        /* Remove all existing data */
        resetAreaOverlayLayer();
        hubMarker.current && hubMarker.current.remove();

        /* Add back the AreaOverlay and HubMarker */
        addAreaOverlay(true);
        addHubPosition();
      }
    }
  }, [
    addAreaOverlay,
    addHubPosition,
    areaOverlay,
    props.region,
    resetAreaOverlayLayer,
  ]);

  const onCreatePolygonDrawing = (polygonData: FeatureCollection) => {
    props.onCreatePolygonDrawing(polygonData, ordersGeo);
  };

  const onUpdatePolygonDrawing = (polygonData: FeatureCollection) => {
    props.onUpdatePolygonDrawing(polygonData, ordersGeo);
  };

  return (
    <div ref={mapContainer} className="map-container">
      {props.hasLegend && (
        <MapboxLegend
          map={map.current}
          mapStyle={mapStyle}
          region={props.region}
          windows={windows}
          changeMapStyle={changeMapStyle}
        />
      )}

      <Validations map={map.current} {...props} />

      {/* Orders Marker */}
      {map.current &&
        styleLoaded &&
        ordersGeo &&
        props.hasOrders &&
        props.orders &&
        props.selectedOrders &&
        props.windowGroups &&
        props.fetchedBundles && (
          <OrdersMarker
            map={map.current}
            hoverPopupRef={hoverPopupRef.current}
            rightClickPopupRef={rightClickPopupRef.current}
            orders={ordersGeo}
            selectedOrders={props.selectedOrders}
            selectedOrderIds={props.selectedOrderIds}
            windowGroups={props.windowGroups}
            windows={windows}
            fetchedBundles={props.fetchedBundles}
            onSelect={props.onClickOrderMarker}
          />
        )}

      {/* Bundle Marker */}
      {map.current &&
        styleLoaded &&
        props.hasBundle &&
        props.bundles &&
        props.fetchedBundles &&
        props.windowGroups &&
        props.selectedOrders && (
          <BundleMarker
            map={map.current}
            hoverPopupRef={hoverPopupRef.current}
            rightClickPopupRef={rightClickPopupRef.current}
            bundles={props.bundles}
            windowGroups={props.windowGroups}
            windows={windows}
            fetchedBundles={props.fetchedBundles}
            selectedOrders={props.selectedOrders}
            selectedOrderIds={props.selectedOrderIds}
            onSelect={props.onClickOrderMarker}
            updateBundleFromPusher={props.updateBundleFromPusher}
          />
        )}

      {/* Multi Marker */}
      {map.current &&
        styleLoaded &&
        multiMarkersGeo &&
        props.hasMultiMarker &&
        props.multiMarkers &&
        props.selectedOrders &&
        props.windowGroups &&
        props.fetchedBundles && (
          <MultiMarker
            map={map.current}
            rightClickPopupRef={rightClickPopupRef.current}
            multiMarkers={multiMarkersGeo}
            selectedOrders={props.selectedOrders}
            selectedOrderIds={props.selectedOrderIds}
            windowGroups={props.windowGroups}
            windows={windows}
            fetchedBundles={props.fetchedBundles}
            onSelect={props.onClickOrderMarker}
          />
        )}

      {/* Draw Polygon */}
      {map.current && props.enableDraw && (
        <DrawPolygon
          map={map.current}
          shouldClearPolygon={props.shouldClearPolygon}
          onReset={props.resetPolygonDrawing}
          onCreate={onCreatePolygonDrawing}
          onUpdate={onUpdatePolygonDrawing}
        />
      )}

      {/* Zones Overlay */}
      {map.current &&
        props.hasZonesOverlay &&
        !zonesOverlay.loading &&
        zonesOverlay.data &&
        props.zonesOverlayObjects && (
          <ZonesOverlay
            map={map.current}
            data={zonesOverlay.data}
            type={props.zonesOverlayType}
            objects={props.zonesOverlayObjects}
            countData={props.zonesOverlayCountData || {}}
            hideLabel={props.zonesOverlayHideLabel}
            onClick={props.onClickZonesOverlay}
          />
        )}

      {/* Pinpoint */}
      {map.current &&
        props.hasPinpoint &&
        (props.pinpointData || props.pinpointIsUsingHubAsDefault) && (
          <Pinpoint
            map={map.current}
            region={props.region}
            data={props.pinpointData}
            draggable={props.pinpointDraggable}
            isUsingHubAsDefault={props.pinpointIsUsingHubAsDefault}
            onDragEnd={props.onDragEndPinpoint}
          />
        )}
    </div>
  );
};
